/**
 * Property Validation Utility
 * Phase 1: Foundation & Type Safety
 */

import {
  NodePropertyDefinition,
  PropertyValidationResult,
} from '../types/node-properties';
import {
  PropertyValidation,
} from '../types/properties';

/**
 * Validates a single property value against its definition
 */
export function validateProperty(
  value: any,
  definition: NodePropertyDefinition
): PropertyValidationResult {
  const errors: Array<{ propertyKey: string; message: string; rule?: any }> = [];

  // Skip validation if readonly or hidden (legacy properties)
  if ((definition as any).readonly || (definition as any).hidden) {
    return { valid: true, errors: [] };
  }

  // Required validation (check definition.required directly in new type system)
  if (definition.required && (value === undefined || value === null || value === '')) {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} is required`,
      rule: 'required',
    });
  }

  // Skip further validation if value is empty and not required
  if (value === undefined || value === null || value === '') {
    return { valid: errors.length === 0, errors };
  }

  // Type-specific validation (only for types that exist in new system)
  switch (definition.type) {
    case 'string':
    case 'text':
      validateStringBasic(value, definition, errors);
      break;
    case 'number':
      validateNumberBasic(value, definition, errors);
      break;
    case 'boolean':
      validateBoolean(value, definition, errors);
      break;
    case 'json':
      validateJson(value, definition, errors);
      break;
    // 'options' and 'multiOptions' are the new names for select/multiselect
    case 'options':
      validateOptions(value, definition, errors);
      break;
    case 'multiOptions':
      validateMultiOptions(value, definition, errors);
      break;
    case 'color':
      validateColor(value, definition, errors);
      break;
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validates multiple properties against their definitions
 */
export function validateProperties(
  properties: Record<string, any>,
  definitions: NodePropertyDefinition[]
): PropertyValidationResult {
  const allErrors: Array<{ propertyKey: string; message: string; rule?: any }> = [];

  for (const definition of definitions) {
    const value = properties[definition.key];
    const result = validateProperty(value, definition);

    if (!result.valid && result.errors) {
      allErrors.push(...result.errors);
    }
  }

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
  };
}

/**
 * Basic string/text validation for new type system
 */
function validateStringBasic(
  value: any,
  definition: NodePropertyDefinition,
  errors: Array<{ propertyKey: string; message: string; rule?: any }>
): void {
  if (typeof value !== 'string') {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} must be a string`,
      rule: 'type',
    });
  }
}

/**
 * Basic number validation for new type system
 */
function validateNumberBasic(
  value: any,
  definition: NodePropertyDefinition,
  errors: Array<{ propertyKey: string; message: string; rule?: any }>
): void {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (typeof numValue !== 'number' || isNaN(numValue)) {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} must be a number`,
      rule: 'type',
    });
  }
}

/**
 * Options (select) validation for new type system
 */
function validateOptions(
  value: any,
  definition: NodePropertyDefinition,
  errors: Array<{ propertyKey: string; message: string; rule?: any }>
): void {
  // For now, just accept any value - the backend will validate against actual options
  // TODO: Add proper options validation when schema is available
}

/**
 * Multi-options validation for new type system
 */
function validateMultiOptions(
  value: any,
  definition: NodePropertyDefinition,
  errors: Array<{ propertyKey: string; message: string; rule?: any }>
): void {
  if (!Array.isArray(value)) {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} must be an array`,
      rule: 'type',
    });
  }
}

// Legacy validation functions removed - using new simplified validators above

/**
 * Boolean validation
 */
function validateBoolean(
  value: any,
  definition: NodePropertyDefinition,
  errors: Array<{ propertyKey: string; message: string; rule?: any }>
): void {
  if (typeof value !== 'boolean') {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} must be a boolean`,
      rule: 'type',
    });
  }
}

// Additional legacy validation functions removed

/**
 * Color validation
 */
function validateColor(
  value: any,
  definition: NodePropertyDefinition,
  errors: Array<{ propertyKey: string; message: string; rule?: any }>
): void {
  if (typeof value !== 'string') {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} must be a string`,
      rule: 'type',
    });
    return;
  }

  // Validate hex color format
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (!hexRegex.test(value)) {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} must be a valid hex color (e.g., #FF0000)`,
      rule: 'pattern',
    });
  }
}

/**
 * JSON validation
 */
function validateJson(
  value: any,
  definition: NodePropertyDefinition,
  errors: Array<{ propertyKey: string; message: string; rule?: any }>
): void {
  if (typeof value === 'string') {
    try {
      JSON.parse(value);
    } catch {
      errors.push({
        propertyKey: definition.key,
        message: `${definition.label} must be valid JSON`,
        rule: 'format',
      });
    }
  } else if (typeof value !== 'object') {
    errors.push({
      propertyKey: definition.key,
      message: `${definition.label} must be a valid JSON object`,
      rule: 'type',
    });
  }
}

/**
 * Apply default values to properties based on definitions
 */
export function applyDefaultValues(
  properties: Record<string, any>,
  definitions: NodePropertyDefinition[]
): Record<string, any> {
  const result = { ...properties };

  for (const definition of definitions) {
    if (result[definition.key] === undefined && definition.default !== undefined) {
      result[definition.key] = definition.default;
    }
  }

  return result;
}

/**
 * Filter out readonly and hidden properties (legacy properties - may not exist in new system)
 */
export function filterEditableProperties(
  definitions: NodePropertyDefinition[]
): NodePropertyDefinition[] {
  return definitions.filter(def => !(def as any).readonly && !(def as any).hidden);
}

/**
 * Group properties by their group field (legacy property - may not exist in new system)
 */
export function groupProperties(
  definitions: NodePropertyDefinition[]
): Record<string, NodePropertyDefinition[]> {
  const groups: Record<string, NodePropertyDefinition[]> = {
    default: [],
  };

  for (const definition of definitions) {
    const groupName = (definition as any).group || 'default';
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(definition);
  }

  // Sort properties within each group by order (legacy property)
  for (const groupName in groups) {
    groups[groupName].sort((a, b) => ((a as any).order || 0) - ((b as any).order || 0));
  }

  return groups;
}
